import { Box } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";

import { Divider } from "../components/Divider";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { ChatPanelMessageContent } from "./ChatPanelMessageContent";
import { ChatAdminReadOnly } from "./components/ChatAdminReadOnly";
import { ChatReplyPreview } from "./components/ChatReplyPreview";
import { useChatPanelState } from "./hooks/useChatPanelState";

export interface ChatPanelProps {
  modalState: UseModalState;
  /**
   * Workplace ID for the chat channel. When omitted, defaults to the
   * logged-in user's profile ID from the session.
   */
  workplaceId?: string;
  /**
   * Worker ID for API integration to fetch/send chat messages
   */
  workerId: string;
  workerName: string;
  /**
   * When provided, loads the placement-specific channel (3-part URL).
   */
  placementId?: string;
  /**
   * Optional callback when chat panel is closed
   */
  onClose?: () => void;
  /**
   * Optional callback when "View Profile" is clicked in the chat header.
   * When provided, the parent handles opening the worker profile (e.g. replacing panels).
   * When omitted, defaults to navigating to the worker profile page.
   */
  onViewProfile?: (workerId: string) => void;
  /**
   * Whether to show the "View Profile" button in the chat header.
   * Defaults to true. Set to false when the chat opens on a page that already shows the worker profile.
   */
  showViewProfile?: boolean;
  /**
   * Optional callback when "Invite to shift" is clicked in the chat input.
   * When provided, shows the invite button in the input area.
   */
  onInviteToShift?: () => void;
}

/**
 * Chat panel component for worker communication.
 * Displays chat messages with a worker and provides an input to send new messages.
 * Connected to Sendbird for real-time messaging.
 */
export function ChatPanel(props: ChatPanelProps) {
  const {
    modalState,
    workplaceId: workplaceIdFromCaller,
    workerId,
    workerName,
    placementId,
    onClose,
    onViewProfile,
    showViewProfile,
    onInviteToShift,
  } = props;

  const {
    workplaceId,
    isAdmin,
    isWorkerSession,
    isLoading,
    isError,
    isBlocked,
    isSendingDisabled,
    messages,
    hasMore,
    isLoadingMore,
    exclusion,
    replyTo,
    handleClose,
    handleSend,
    handleFileSelect,
    handleRetry,
    handleMarkMessageAsUnread,
    handleMarkConversationAsUnread,
    canMarkConversationAsUnread,
    loadMore,
    setReplyTo,
    clearReply,
  } = useChatPanelState({
    modalState,
    workplaceId: workplaceIdFromCaller,
    workerId,
    placementId,
    onClose,
  });

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Box sx={{ padding: 5 }}>
        <ChatHeader
          workerId={workerId}
          workerName={workerName}
          showViewProfile={showViewProfile}
          onClose={handleClose}
          onViewProfile={onViewProfile}
          onMarkConversationAsUnread={
            canMarkConversationAsUnread && !isAdmin && !isBlocked && !isWorkerSession
              ? handleMarkConversationAsUnread
              : undefined
          }
        />
      </Box>

      <Divider />

      <Box
        role="log"
        aria-label="Chat messages"
        aria-live="polite"
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: 5,
          display: "flex",
          flexDirection: "column-reverse",
          gap: 4,
        }}
      >
        <ChatPanelMessageContent
          messages={messages}
          workerName={workerName}
          workplaceId={workplaceId}
          isLoading={isLoading}
          isError={isError}
          isBlocked={isBlocked}
          isAdmin={isAdmin}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          exclusion={exclusion}
          onRetry={handleRetry}
          onLoadMore={loadMore}
          onReply={setReplyTo}
          onMarkAsUnread={
            isAdmin || isBlocked || isWorkerSession ? undefined : handleMarkMessageAsUnread
          }
        />
      </Box>

      {replyTo && !isAdmin && !isBlocked && (
        <ChatReplyPreview
          message={replyTo.message}
          senderName={replyTo.senderName}
          onCancel={clearReply}
        />
      )}

      {!isBlocked && (
        <Box sx={{ padding: 5 }}>
          {isAdmin ? (
            <ChatAdminReadOnly />
          ) : (
            <ChatInput
              disabled={isSendingDisabled}
              onFileSelect={handleFileSelect}
              onInviteToShift={onInviteToShift}
              onSend={handleSend}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
